import { defineStore } from 'pinia'
import authService, { LoginCredentials, TwoFactorVerifyRequest } from '../services/auth-service'
import { loadUserMenus } from '../components/sidebar/NavigationRoutes'
import menuService from '../services/menu-service'
import { useOUStore } from './ou-store'
import ouService from '../services/ou-service'

interface Role {
  id: number
  name: string
  description?: string
}

interface User {
  id: number
  name: string
  email: string
  created_at: string
  roles?: Role[]
  permissions?: Record<string, any>
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  loading: boolean
  isLoggingOut: boolean
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    token: localStorage.getItem('auth_token'),
    isAuthenticated: !!localStorage.getItem('auth_token'),
    loading: false,
    isLoggingOut: false,
  }),

  getters: {
    currentUser: (state) => state.user,
    isLoggedIn: (state) => state.isAuthenticated && !!state.token,
    userName: (state) => state.user?.name || '',
    userEmail: (state) => state.user?.email || '',
    userRoles: (state) => state.user?.roles?.map((role) => role.name) || [],
    userPermissions: (state) => state.user?.permissions || {},
  },

  actions: {
    async login(credentials: LoginCredentials) {
      this.loading = true
      try {
        const response = await authService.login(credentials)

        if (response.status && response.data) {
          // Check if 2FA is required
          if (response.requires_2fa && response.data.user_id) {
            return {
              success: true,
              requires_2fa: true,
              user_id: response.data.user_id,
              message: response.message,
            }
          }

          // Normal login (no 2FA)
          if (response.data.token && response.data.user) {
            await this.completeLogin(response.data.token, response.data.user)
            return { success: true, requires_2fa: false, message: response.message }
          }
        }

        return { success: false, message: response.message || 'Login failed' }
      } catch (error: any) {
        console.error('Login error:', error)
        const message = error.response?.data?.message || 'An error occurred during login'
        return { success: false, message }
      } finally {
        this.loading = false
      }
    },

    async verify2FA(data: TwoFactorVerifyRequest) {
      this.loading = true
      try {
        const response = await authService.verify2FA(data)

        if (response.status && response.data?.token && response.data?.user) {
          await this.completeLogin(response.data.token, response.data.user)
          return { success: true, message: response.message }
        }

        return { success: false, message: response.message || '2FA verification failed' }
      } catch (error: any) {
        console.error('2FA verification error:', error)
        const message = error.response?.data?.message || 'Invalid verification code'
        return { success: false, message }
      } finally {
        this.loading = false
      }
    },

    async completeLogin(token: string, user: User) {
      this.token = token
      this.user = user
      this.isAuthenticated = true

      localStorage.setItem('auth_token', token)
      localStorage.setItem('user', JSON.stringify(user))

      // Initialize OU store after successful login
      try {
        const ouStore = useOUStore()
        await ouStore.initialize()
      } catch (ouError) {
        console.error('Failed to initialize OU store:', ouError)
      }

      // Load user menus after successful login
      try {
        await loadUserMenus()
      } catch (menuError) {
        console.error('Failed to load menus:', menuError)
      }
    },

    async logout() {
      this.isLoggingOut = true
      localStorage.setItem('is_logging_out', 'true')
      this.loading = true

      try {
        // Clear server-side OU cache BEFORE logout (while token is still valid)
        try {
          await ouService.clearCache()
        } catch (e) {
          // Ignore errors - not critical
          console.debug('Failed to clear server OU cache:', e)
        }

        await authService.logout()
      } catch (error) {
        console.error('Logout error:', error)
      } finally {
        this.clearAuth()
        this.loading = false
        this.isLoggingOut = false
        localStorage.removeItem('is_logging_out')
      }
    },

    clearAuth() {
      this.user = null
      this.token = null
      this.isAuthenticated = false
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
      // Clear menu cache on logout
      menuService.clearCache()
      // Clear OU cache on logout (local only, server cache already cleared)
      const ouStore = useOUStore()
      ouStore.clearCache()
    },

    async fetchUser() {
      if (!this.token) return

      this.loading = true
      try {
        const response = await authService.getUser()
        if (response.status && response.data) {
          this.user = response.data
          this.isAuthenticated = true
          localStorage.setItem('user', JSON.stringify(response.data))
        }
      } catch (error) {
        console.error('Fetch user error:', error)
        this.clearAuth()
      } finally {
        this.loading = false
      }
    },

    async refreshToken() {
      if (!this.token) return

      try {
        const response = await authService.refreshToken()
        if (response.status && response.data && response.data.token) {
          this.token = response.data.token
          localStorage.setItem('auth_token', response.data.token)
          return true
        }
        return false
      } catch (error) {
        console.error('Refresh token error:', error)
        this.clearAuth()
        return false
      }
    },

    initializeAuth() {
      const token = localStorage.getItem('auth_token')
      const userStr = localStorage.getItem('user')

      if (token && userStr) {
        try {
          this.token = token
          this.user = JSON.parse(userStr)
          this.isAuthenticated = true
          // Optionally fetch fresh user data
          this.fetchUser()
        } catch (error) {
          console.error('Initialize auth error:', error)
          this.clearAuth()
        }
      }
    },
  },
})
